import { LayoutElementBasedCarousel } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/LayoutElementBasedCarousel";
import { useSettingAndAssignMigrateToLayoutServiceEndpoints } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingAndAssignMigrateToLayoutServiceEndpoints";
import { ProductTileImpressionProvider } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/ProductTileImpressionsProvider";
import { useIsLoggedOutDiscoverPage } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useIsLoggedOutDiscoverPage";
import { useIsLoggedOutDiscoverPerfAppTreatment } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useIsLoggedOutDiscoverPerfAppTreatment";
import { useSearchPageIsFromDiscoverURL } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";
import { useSearchPageParsedSearch } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageParsedSearch";
import { useDiscoverPageShouldShowTopProducts } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/util/useDiscoverPageShouldShowTopProducts";
import { LoadingSpinner } from "@faire/slate/components/loading-spinner";
import { useQuery_escape_hatch as useBackendServiceDiscoverPageLayoutQuery } from "@faire/web-api--source/endpoints/www/api/layout/discover-page/post-hooks";
import { useQuery_escape_hatch as useLayoutServiceDiscoverPageLayoutQuery } from "@faire/web-api--source/endpoints/www/api/v3/layout/discover-page/post-hooks";
import { IDiscoverPageLayoutRequest } from "@faire/web-api--source/indigofair/data/IDiscoverPageLayoutRequest";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useMemo } from "react";
import styled from "styled-components";

export const SearchPageFeaturedProducts = () => {
  const { isMobile } = useViewport();
  const parsedSearch = useSearchPageParsedSearch();
  const featuredProductToken = useMemo(
    () => getRefPGivenRecordOfQueryParams(parsedSearch),
    [parsedSearch]
  );

  const isDiscover = useSearchPageIsFromDiscoverURL();
  const shouldCallDiscoverPageLayoutQuery =
    !!featuredProductToken && isDiscover;
  const isUsingLayoutService =
    useSettingAndAssignMigrateToLayoutServiceEndpoints();

  const isLoggedOutDiscoverPage = useIsLoggedOutDiscoverPage();
  const isShowingSectionalDesign = useDiscoverPageShouldShowTopProducts();
  //This will make sure it is variant 2
  const isPerfDiscoverPageTreatment = useIsLoggedOutDiscoverPerfAppTreatment();
  const hideProductGridHeader =
    isLoggedOutDiscoverPage &&
    (isShowingSectionalDesign || isPerfDiscoverPageTreatment);

  const request = IDiscoverPageLayoutRequest.build({
    featured_product_token: featuredProductToken,
  });

  const { data: backendData, isPending: isBackendServiceLayoutLoading } =
    useBackendServiceDiscoverPageLayoutQuery(request, {
      enabled: shouldCallDiscoverPageLayoutQuery && !isUsingLayoutService,
    });

  const { data: layoutData, isPending: isLayoutServiceLayoutLoading } =
    useLayoutServiceDiscoverPageLayoutQuery(request, {
      enabled: shouldCallDiscoverPageLayoutQuery && isUsingLayoutService,
    });

  if (!shouldCallDiscoverPageLayoutQuery) {
    return null;
  }

  const isDiscoverPageLayoutLoading = isUsingLayoutService
    ? isLayoutServiceLayoutLoading
    : isBackendServiceLayoutLoading;
  const discoverPageLayoutData = isUsingLayoutService
    ? layoutData
    : backendData;

  const requestId = discoverPageLayoutData?.request_id ?? "";
  const element = discoverPageLayoutData?.layout_elements.find(
    (e) => e.type === ILayoutElement.Type.PRODUCT_TILE_CAROUSEL
  );
  if (isDiscoverPageLayoutLoading) {
    return (
      <LoadingSpinnerWrapper>
        <LoadingSpinner size="xLarge" />
      </LoadingSpinnerWrapper>
    );
  } else if (element) {
    return (
      <>
        <ProductTileImpressionProvider
          requestId={requestId}
          onClickEventName={element.on_click_event_name}
          featuredProductToken={featuredProductToken}
        >
          <Spacer height="4x" />
          <SectionHeader>
            <StrictLocalMsg
              id="eHpOFR"
              defaultMessage="Featured products"
              description={{
                text: "Title for a section of featured product tiles on a landing page",
              }}
            />
          </SectionHeader>
          <Spacer height="1x" />
          <LayoutElementBasedCarousel
            layoutElement={element}
            requestId={requestId}
            hideTitle
            noWrap
            showArrowCTA={isMobile}
            itemsPerSlide={{
              mobileAndAbove: 2,
              tabletAndAbove: 4,
              desktopAndAbove: 5,
              xLargeAndAbove: 6,
            }}
          />
          <Spacer height="4x" />
          {hideProductGridHeader ? null : (
            <SectionHeader>
              <StrictLocalMsg
                id="O6BXIY"
                defaultMessage="All products"
                description={{
                  text: "Title for a section of product tiles on a landing page",
                }}
              />
            </SectionHeader>
          )}
        </ProductTileImpressionProvider>
      </>
    );
  } else {
    return null;
  }
};

const getRefPGivenRecordOfQueryParams = (
  parsedSearch: Record<string, string | undefined>
) => {
  return parsedSearch.refP;
};

const LoadingSpinnerWrapper = styled(Column).attrs({
  align: "center",
  justify: "center",
})`
  height: 400px;
  width: 100%;
`;

const SectionHeader: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <Typography
      as="h2"
      variant={{
        mobileAndAbove: "pageHeaderSerifRegular",
        tabletAndAbove: "displaySSerifRegular",
      }}
    >
      {children}
    </Typography>
  );
};
